import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Input, Table } from 'reactstrap';

import customerApi, { IContactPersonRow } from 'api/contacts';
import translations from 'services/translations/translations';

type Field =
  | 'name'
  | 'title'
  | 'jobDescription'
  | 'office'
  | 'operatingArea'
  | 'email'
  | 'phone';

/**
 * The contact persons of a company on its page as a list: name, title, office and area,
 * email and phone, each column with its search below its name. A row opens the person;
 * the email and the phone open the mail program and the call.
 */
const CompanyPersons = ({
  contactId,
  names,
}: {
  contactId: string;
  // The persons as given with the company, shown by name if their details cannot be read
  names: { firstName: string; id: number; lastName: string }[];
}): JSX.Element => {
  const { t } = useTranslation([translations.contacts, translations.common]);
  const history = useHistory();

  const [rows, setRows] = useState<IContactPersonRow[] | null>(null);
  const [search, setSearch] = useState<Partial<Record<Field, string>>>({});

  useEffect(() => {
    customerApi
      .searchPersons({ contactId: Number(contactId), limit: 250, offset: 0 })
      // Only the persons of this company, whatever the server returns
      .then((result) =>
        setRows(
          result.rows.filter(
            (person) => String(person.contactId) === String(contactId)
          )
        )
      )
      .catch(() => setRows(null));
  }, [contactId]);

  const shown: IContactPersonRow[] =
    rows ??
    names.map((person) => ({
      companyName: '',
      contactId: Number(contactId),
      email: null,
      firstName: person.firstName,
      id: person.id,
      jobDescription: null,
      lastName: person.lastName,
      office: null,
      operatingArea: null,
      phone: null,
      phoneWithCountryCode: null,
      title: null,
    }));

  const phoneOf = (person: IContactPersonRow) =>
    person.phone || person.phoneWithCountryCode || '';

  // The text of a column, as searched
  const textOf = (person: IContactPersonRow, field: Field): string => {
    switch (field) {
      case 'name':
        return `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
      case 'office':
        return person.office ?? (rows ? t('All areas') : '');
      case 'phone':
        return phoneOf(person);
      default:
        return person[field] ?? '';
    }
  };

  // Office and area only when the company has them
  const fields: Field[] = (
    [
      'name',
      'title',
      'jobDescription',
      'office',
      'operatingArea',
      'email',
      'phone',
    ] as Field[]
  ).filter(
    (field) =>
      (field !== 'office' && field !== 'operatingArea') ||
      shown.some((person) => person[field])
  );

  const titles: Record<Field, string> = {
    email: t('Email'),
    jobDescription: t('Job Description'),
    name: t('Name'),
    office: t('Office'),
    operatingArea: t('Operating area'),
    phone: t('Phone'),
    title: t('Title'),
  };

  const filtered = shown
    .filter((person) =>
      fields.every((field) => {
        const wanted = search[field]?.trim().toLowerCase();
        if (!wanted) {
          return true;
        }
        const text = textOf(person, field).toLowerCase();
        return field === 'phone'
          ? text.replace(/\s/g, '').includes(wanted.replace(/\s/g, ''))
          : text.includes(wanted);
      })
    )
    .sort((a, b) => textOf(a, 'name').localeCompare(textOf(b, 'name')));

  // A click on a link or a search does not open the person
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();

  if (shown.length === 0) {
    return <p className="company-persons-none">{t('No Contacts')}</p>;
  }

  return (
    <div className="table-scroll company-persons">
      <Table size="sm" hover className="clean-table company-persons-table">
        <thead>
          <tr>
            {fields.map((field) => (
              <th key={field}>
                <div className="contacts-column-header">
                  <span>{titles[field]}</span>
                  <Input
                    aria-label={titles[field]}
                    bsSize="sm"
                    onChange={(event) => {
                      // Read before the event is reused (React 16)
                      const { value } = event.target;
                      setSearch((current) => ({ ...current, [field]: value }));
                    }}
                    placeholder={t('Search column')}
                    type="search"
                    value={search[field] ?? ''}
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((person) => (
            <tr
              key={person.id}
              onClick={() => history.push(`/contacts/persons/${person.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {fields.map((field) => {
                const text = textOf(person, field);
                if (field === 'name') {
                  return (
                    <td key={field}>
                      <strong>{text}</strong>
                    </td>
                  );
                }
                if (field === 'email' && text) {
                  return (
                    <td key={field}>
                      <a href={`mailto:${text}`} onClick={stop}>
                        {text}
                      </a>
                    </td>
                  );
                }
                if (field === 'phone' && text) {
                  return (
                    <td key={field} className="text-nowrap">
                      <a href={`tel:${text.replace(/\s/g, '')}`} onClick={stop}>
                        {text}
                      </a>
                    </td>
                  );
                }
                return <td key={field}>{text}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default CompanyPersons;
